import { wellKnownRoutes } from '@/routes/wellknown';
import { useImmersveAuthStore } from '@/stores/auth.store';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { wellKnownKycSessionRoutes } from '@/routes/wellKnownKycSessionRoutes';
import { redirectToTimeoutWithSessionId } from '@/util/timeoutRedirector';
import type { RouteLocationNormalized, RouteMeta, RouteRecordNameGeneric } from 'vue-router';
import type { KycSession } from '@/models/KycSession';

// eslint-disable-next-line complexity
export async function beforeResolveGuard(to: RouteLocationNormalized) {
  const { name: route, meta = {} } = to ?? {};

  if ((route && wellKnownRoutes[route]) || route === wellKnownKycSessionRoutes.Initiation) {
    return true;
  }

  const sessionId = to.params.sessionid as string;
  if (to?.meta?.requiresAuth) {
    const authStore = useImmersveAuthStore();
    if (authStore.hasInvalidAccessToken()) {
      await authStore.refreshTokens();
    }
  }

  const kycSessionStore = useKycSessionStore();
  await kycSessionStore.getSessionData();

  if (!kycSessionStore.kycSession) {
    return {
      name: wellKnownRoutes.Timeout,
      replace: true,
    };
  }
  const kycSession = kycSessionStore.kycSession;
  const screens = kycSessionStore.kycSession.steps.screens;

  if (!screens.includes(route as string)) {
    return redirectToTimeoutWithSessionId({
      kycSession,
      replace: true,
    });
  }

  if (!hasValidKyc(meta, kycSession)) {
    return redirectToTimeoutWithSessionId({
      kycSession: kycSessionStore.kycSession,
      replace: true,
    });
  }

  if (shouldRedirectToRegionStep(route, kycSession)) {
    return {
      name: wellKnownKycSessionRoutes.Region,
      replace: true,
      params: {
        sessionid: sessionId,
      },
    };
  }
}

export function hasValidKyc(meta: RouteMeta, kycSession: KycSession) {
  const requiresKycStatus = meta?.requiresKycStatus;
  const requiresKycCheckStatus = meta?.requiresKycCheckStatus;
  const isValidKycStatus = Array.isArray(requiresKycStatus);
  const hasKycSteps = kycSession.steps.hasKycSteps();

  if (!isValidKycStatus || !hasKycSteps) return true;

  const kycStatus = kycSession.status;
  const kycCheckStatus = kycSession.kycCheck?.status;

  const isStatusMatch = requiresKycStatus.includes(kycStatus);
  const isCheckStatusMatch = !requiresKycCheckStatus || requiresKycCheckStatus === kycCheckStatus;

  return isStatusMatch && isCheckStatusMatch;
}

export function shouldRedirectToRegionStep(route: RouteRecordNameGeneric, kycSession: KycSession) {
  if (route === wellKnownKycSessionRoutes.Region) {
    return false;
  }

  if (kycSession?.region) {
    return false;
  }

  return kycSession.steps.hasRegionStep();
}
